import React, { useRef, useEffect } from "react";
import PropTypes from "kf-proptypes/widgets";
import { t } from "@lingui/macro";

import { FieldLabel, FieldHint, FieldErrors } from "../fields/index.jsx";

import styles from "./multiline.css";

export const MULTILINE_TYPE = {
  NORMAL: "normal",
  MINIMAL: "minimal"
};

/**
 * MultiLine is a component that renders a multi-line text input field with various functionalities.
 */

export function MultiLine(props) {
  const {
    name,
    value,
    className,
    label,
    required,
    hint,
    errors = [],
    id,
    style = {},
    disabled,
    autoGrow,
    autoFocus,
    onChange,
    onFocus,
    onBlur,
    onKeyUp,
    onKeyDown,
    onKeyPress,
    type = MULTILINE_TYPE.NORMAL,
    readOnly = false,
    maxLength = 250,
    placeholder,
    minHeight = "auto",
    showCharacterLimitHint = false,
    labelLeftIcon,
    labelRightIcon,
    ...restProps
  } = props;
  const autogrowingTextarea = useRef(null);
  useEffect(
    function autoGrowEffect() {
      if (autogrowingTextarea.current) {
        autogrowingTextarea.current.style.height = minHeight;
        autogrowingTextarea.current.style.height = `${autogrowingTextarea.current.scrollHeight}px`;
      }
    },
    [value, autoGrow, minHeight]
  );
  return (
    <div className={styles.container}>
      {label && (
        <FieldLabel
          label={label}
          disabled={disabled}
          targetId={id}
          required={required}
          leftIcon={labelLeftIcon}
          rightIcon={labelRightIcon}
        />
      )}
      <div
        className={`${className} ${styles.multilineWrapper} ${styles[type]}  ${
          errors.length > 0 ? styles.error : ""
        } ${readOnly ? styles.readOnly : ""}             ${
          disabled ? styles.disabled : ""
        }`}
        data-component="multiline"
        role="textbox"
        aria-required={required}
        aria-invalid={errors && errors.length > 0}
        aria-labelledby={`${id}_label`}
        aria-describedby={`${id}_hint`}
        aria-errormessage={`${id}_errors`}
        aria-live="assertive"
        {...restProps}
      >
        {autoGrow ? (
          <textarea
            name={name}
            disabled={disabled}
            onInput={onChange}
            ref={autogrowingTextarea}
            autoFocus={autoFocus}
            value={value}
            style={style}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyUp={onKeyUp}
            onKeyDown={onKeyDown}
            onKeyPress={onKeyPress}
            id={id}
            maxLength={maxLength}
            placeholder={placeholder}
            readOnly={readOnly}
          />
        ) : (
          <div className={styles.multilineScrollbar} style={style}>
            <textarea
              name={name}
              className={styles.textAreaAutoGrow}
              disabled={disabled}
              onInput={onChange}
              ref={autogrowingTextarea}
              value={value}
              autoFocus={autoFocus}
              onFocus={onFocus}
              onBlur={onBlur}
              onKeyUp={onKeyUp}
              onKeyDown={onKeyDown}
              onKeyPress={onKeyPress}
              id={id}
              maxLength={maxLength}
              placeholder={placeholder}
              readOnly={readOnly}
            />
          </div>
        )}
      </div>
      {showCharacterLimitHint && (
        <span
          className={`${styles.maxLengthHint} ${
            value?.length === maxLength ? styles.maxLengthLimitText : ""
          }`}
          id={`${id}_length_hint`}
        >
          {value?.length === maxLength
            ? t`You have reached the maximum limit of ${maxLength} characters.`
            : t`${value ? value?.length : 0} of ${maxLength} characters left.`}
        </span>
      )}
      {hint && <FieldHint hint={hint} targetId={id} />}
      {errors && errors.length > 0 && (
        <FieldErrors errors={errors} targetId={id} />
      )}
    </div>
  );
}

MultiLine.propTypes = {
  /**
   * Name of the multi-line input field.
   */
  name: PropTypes.string,
  /**
   * Current value of the multi-line input field.
   */
  value: PropTypes.string,
  /**
   * Additional class name for the multi-line input field.
   */
  className: PropTypes.string,
  /**
   * Label text for the multi-line input field.
   */
  label: PropTypes.string,
  /**
   * Determines if the multi-line input field is required.
   */
  required: PropTypes.bool,
  /**
   * Hint text for the multi-line input field.
   */
  hint: PropTypes.string,
  /**
   * Array of error messages associated with the multi-line input field.
   */
  errors: PropTypes.arrayOf(PropTypes.string),
  /**
   * ID attribute for the multi-line input field.
   */
  id: PropTypes.string,

  /**
   * Inline style object for the multi-line input field.
   */
  style: PropTypes.shape({
    display: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  /**
   * Determines if the multi-line input field is disabled.
   */
  disabled: PropTypes.bool,

  /**
   * Determines if the multi-line input field should automatically grow in height based on content.
   */
  autoGrow: PropTypes.bool,

  /**
   * Determines if the multi-line input field should be automatically focused on mount.
   */
  autoFocus: PropTypes.bool,

  /**
   * Event handler for the onChange event.
   */
  onChange: PropTypes.func,
  /**
   * Event handler for the onFocus event.
   */
  onFocus: PropTypes.func,
  /**
   * Event handler for the onBlur event.
   */
  onBlur: PropTypes.func,
  /**
   * Event handler for the onKeyUp event.
   */
  onKeyUp: PropTypes.func,
  /**
   * Event handler for the onKeyDown event.
   */
  onKeyDown: PropTypes.func,
  /**
   * Event handler for the onKeyPress event.
   */
  onKeyPress: PropTypes.func,
  /**
   * Type of the multi-line input field. E.g., "text", "password", etc.
   */
  type: PropTypes.string,
  /**
   * Placeholder text for the multi-line input field.
   */
  placeholder: PropTypes.string,
  /**
   * The minimum height for the multi-line input field.
   */
  minHeight: PropTypes.string,
  /**
   * It will allow us to change the read/write multi-line input field.
   */
  readOnly: PropTypes.bool,
  /**
   * It will allow us to shows/hide the character limitation hint for the multi-line input field.
   */
  showCharacterLimitHint: PropTypes.bool,
  /**
   * The maximum character for the multi-line input field.
   */
  maxLength: PropTypes.number,
  /** it will render left icon of label field */
  labelLeftIcon: PropTypes.node,
  /** it will render right icon of label field */
  labelRightIcon: PropTypes.node
};

MultiLine.defaultProps = {
  errors: [],
  style: {},
  type: MULTILINE_TYPE.NORMAL,
  readOnly: false,
  maxLength: 250,
  minHeight: "auto",
  showCharacterLimitHint: false
};
